import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { CalendarContext } from '../context/CalendarContext';

const WARNING_GROUP_ORDER = [
  'rotation_opening',
  'rest_between_days',
  'freeday_pattern',
  'employee_day',
  'day_shift',
  'day_coverage',
  'day',
  'employee_period',
  'global',
];

const WARNING_GROUP_LABELS = {
  rotation_opening: 'Rotación de quincena',
  rest_between_days: 'Descanso entre jornadas',
  freeday_pattern: 'Patrón de días libres',
  employee_day: 'Advertencias por empleado y día',
  day_shift: 'Turnos del día',
  day_coverage: 'Cobertura del día',
  day: 'Advertencias generales del día',
  employee_period: 'Advertencias del empleado en el período',
  global: 'Advertencias generales',
};

const getWarningGroupKey = (warningItem) => {
  const message = String(warningItem?.message || '').toLowerCase();
  const category = warningItem?.scope?.category;
  if (category && WARNING_GROUP_LABELS[category]) {
    return category;
  }

  const kind = warningItem?.scope?.kind;
  if (kind === 'day') {
    if (message.includes('no hay cobertura completa de 24h')) {
      return 'day_coverage';
    }
    if (message.includes('el turno') && message.includes('no está correctamente cubierto')) {
      return 'day_shift';
    }
  }

  if (kind && WARNING_GROUP_LABELS[kind]) {
    return kind;
  }

  return 'global';
};

const CalendarEvents = () => {
  const {
    calendarErrors: errors,
    calendarWarnings: warnings,
    calendarWarningItems,
  } = useContext(CalendarContext);
//  console.log("🧠 CalendarEvents: errores", errors);
//  console.log("🧠 CalendarEvents: advertencias", warnings);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredErrors = useMemo(() => {
    if (!normalizedQuery) return errors;
    return errors.filter((error) => String(error).toLowerCase().includes(normalizedQuery));
  }, [errors, normalizedQuery]);

  const warningGroups = useMemo(() => {
    const allItems = Array.isArray(calendarWarningItems) ? calendarWarningItems : [];
    const items = normalizedQuery
      ? allItems.filter((item) => String(item?.message || '').toLowerCase().includes(normalizedQuery))
      : allItems;
    const grouped = new Map();

    items.forEach((item) => {
      const key = getWarningGroupKey(item);
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(item);
    });

    const orderedKnownGroups = WARNING_GROUP_ORDER
      .filter((key) => grouped.has(key))
      .map((key) => ({
        key,
        label: WARNING_GROUP_LABELS[key] || 'Advertencias',
        items: grouped.get(key) || [],
      }));

    const unknownGroups = Array.from(grouped.entries())
      .filter(([key]) => !WARNING_GROUP_ORDER.includes(key))
      .map(([key, items]) => ({
        key,
        label: WARNING_GROUP_LABELS[key] || 'Advertencias',
        items,
      }));

    return [...orderedKnownGroups, ...unknownGroups];
  }, [calendarWarningItems, normalizedQuery]);

  const handleToggleGroup = (groupKey) => (_event, expanded) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !expanded,
    }));
  };

  const totalMatches = filteredErrors.length + warningGroups.reduce((sum, g) => sum + g.items.length, 0);
  const hasAnyAlert = errors.length > 0 || warnings.length > 0;

  return (
    <Box>
      {hasAnyAlert && (
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar por CI, nombre, fecha, texto..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="disabled" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}

      {normalizedQuery && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {totalMatches} resultado{totalMatches === 1 ? '' : 's'} para "{searchQuery}"
        </Typography>
      )}

      {normalizedQuery && totalMatches === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
          No se encontraron alertas que coincidan con la búsqueda.
        </Typography>
      )}

      {/* Mostrar errores si existen */}
      {filteredErrors.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'error.main' }}>
            Errores
          </Typography>
          {filteredErrors.map((error, index) => (
            <Alert key={`error-${index}`} severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ))}
        </>
      )}

      {/* Mostrar advertencias si existen */}
      {warningGroups.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2, mb: 1, color: 'warning.main' }}>
            Advertencias
          </Typography>
          {warningGroups.map((group) => (
            <Accordion
              key={group.key}
              disableGutters
              elevation={0}
              expanded={!collapsedGroups[group.key]}
              onChange={handleToggleGroup(group.key)}
              sx={{
                mb: 1.5,
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 2,
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'warning.dark' }} />}
                sx={{
                  px: 1.5,
                  py: 0.25,
                  bgcolor: 'warning.50',
                  '& .MuiAccordionSummary-content': { my: 1 },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      textTransform: 'uppercase',
                      color: 'warning.dark',
                    }}
                  >
                    {group.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={group.items.length}
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                    }}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1, py: 1, bgcolor: 'background.paper' }}>
                {group.items.map((warning, index) => (
                  <Alert key={`warn-${group.key}-${index}`} severity="warning" sx={{ mb: 1 }}>
                    {warning.message}
                  </Alert>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </Box>
  );
};

export default CalendarEvents;
